import { useState, useEffect, useRef, useCallback } from "react";
import { fmtClockEST } from "../utils/dateUtils";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions, PERMISSIONS, MENU_KEYS } from "../hooks/usePermissions";
import { Button } from "./ui/button";
import { VersionBadge } from "./VersionBadge";
import axios from "axios";
import InboundCallManager from "./InboundCallManager";
import RecentActivityPanel from "./RecentActivityPanel";
import PhoneDialer from "./PhoneDialer";
import NotificationBell from "./NotificationBell";
import {
  ClipboardList, 
  History, 
  Users, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  Settings,
  Building2,
  ScrollText,
  LayoutDashboard,
  Cog,
  Calendar,
  Trophy,
  Mail,
  FileUser,
  UserCircle,
  FileStack,
  Briefcase,
  UserPlus,
  Plus,
  FileType,
  CheckSquare,
  Share2,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Users2,
  Inbox,
  Phone,
  PhoneCall,
  Bot,
  Zap,
  BookOpen,
  Radio,
  BarChart3,
  Sparkles,
  Link2,
  Megaphone,
  Search,
  Radar,
  ShieldCheck,
  Linkedin,
  Wand2,
  Send
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function DashboardLayout() {
  const { user, logout, isAdmin, isTeamLeader } = useAuth();
  const { hasPermission, canAccessMenu, loading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dbInfo, setDbInfo] = useState(null);
  const [dbTooltipOpen, setDbTooltipOpen] = useState(false);
  const tooltipRef = useRef(null);
  const navRef = useRef(null);
  const navScrollRef = useRef(0);
  const location = useLocation();

  // Restore sidebar scroll position only when explicitly saved (toggle/nav click)
  const shouldRestoreScroll = useRef(false);
  useEffect(() => {
    if (shouldRestoreScroll.current && navRef.current) {
      navRef.current.scrollTop = navScrollRef.current;
      shouldRestoreScroll.current = false;
    }
  });

  const handleNavClick = useCallback(() => {
    if (navRef.current) {
      navScrollRef.current = navRef.current.scrollTop;
      shouldRestoreScroll.current = true;
    }
    setSidebarOpen(false);
  }, []);

  // Collapsible menu state - persist expanded states in localStorage
  const useSectionState = (key, defaultValue) => {
    const [expanded, setExpanded] = useState(() => {
      const saved = localStorage.getItem(key);
      return saved !== null ? saved === 'true' : defaultValue;
    });
    useEffect(() => {
      localStorage.setItem(key, expanded.toString());
    }, [expanded, key]);
    return [expanded, setExpanded];
  };

  const [logsExpanded, setLogsExpanded] = useSectionState('logsSectionExpanded', true);
  const [crmExpanded, setCrmExpanded] = useSectionState('crmSectionExpanded', false);
  const [aiCallsExpanded, setAiCallsExpanded] = useSectionState('aiCallsSectionExpanded', false);
  const [contentExpanded, setContentExpanded] = useSectionState('contentSectionExpanded', false);
  const [teamMgmtExpanded, setTeamMgmtExpanded] = useSectionState('teamMgmtSectionExpanded', false);
  const [adminExpanded, setAdminExpanded] = useSectionState('adminSectionExpanded', false);
  const [reportsExpanded, setReportsExpanded] = useSectionState('reportsSectionExpanded', false);
  const [marketingExpanded, setMarketingExpanded] = useSectionState('marketingSectionExpanded', false);

  // Permission-based access checks (use RBAC first, fallback to team-based for legacy)
  const userTeamNames = user?.team_names || [];

  // Recruiting team member: belongs to a recruiting team (any role except admin)
  const isRecruitingTeam = !isAdmin && userTeamNames.some(n => /recruit/i.test(n));
  // Recruiting Team Leader specifically
  const isRecruitingTL = isTeamLeader && isRecruitingTeam;

  // CRM access: ALL authenticated users have access
  const hasCRMAccess = true;

  // Franchise access: ALL authenticated users have access
  const hasFranchiseAccess = true;

  // Document access: ALL users
  const hasDocumentAccess = true;
  
  // Template access: ALL users
  const hasTemplateAccess = true;
  
  // Social media access: ALL users
  const hasSocialAccess = true;
  
  // Admin access for user management, teams, settings, backup
  const hasUserManagementAccess = hasPermission(PERMISSIONS.ADMIN_USERS_READ);
  const hasTeamManagementAccess = hasPermission(PERMISSIONS.ADMIN_TEAMS_READ);
  const hasSettingsAccess = hasPermission(PERMISSIONS.ADMIN_SETTINGS_READ);
  const hasBackupAccess = hasPermission(PERMISSIONS.ADMIN_BACKUP_READ);

  // Update time every second for real-time display
  useEffect(() => {
    // The header only shows HH:MM, so only re-render when the minute changes.
    const timer = setInterval(() => {
      setCurrentDateTime((prev) => {
        const now = new Date();
        return Math.floor(now.getTime() / 60000) === Math.floor(prev.getTime() / 60000) ? prev : now;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch DB mode info (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/api/system/db-info`, { withCredentials: true })
      .then(r => setDbInfo(r.data))
      .catch(() => {});
  }, [isAdmin]);

  // Close tooltip on outside click
  useEffect(() => {
    if (!dbTooltipOpen) return;
    const handler = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setDbTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dbTooltipOpen]);

  const formatDateTime = (date) => fmtClockEST(date);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // 1. Daily Log group (expanded by default, all users)
  const logsNavItems = [
    { to: "/app/log", icon: ClipboardList, label: "Daily Log" },
    { to: "/app/history", icon: History, label: "History" },
    { to: "/app/leaderboard", icon: Trophy, label: "Leaderboard" },
    { to: "/app/profile", icon: Settings, label: "Profile" },
    hasSocialAccess && { to: "/admin/social", icon: Share2, label: "Social", menu: "social" },
  ].filter(Boolean);

  // `menu` = the same RBAC menuKey the item's route is guarded with in App.js. An item whose
  // route would bounce the user is hidden (while permissions load, show it — no flicker).
  const allowed = (items) =>
    items.filter((item) => !item.menu || permissionsLoading || canAccessMenu(item.menu));

  // 2. CRM group (collapsed by default, CRM users)
  const crmNavItems = [
    { to: "/admin/companies", icon: Briefcase, label: "Companies", menu: "crm_companies" },
    { to: "/admin/leads", icon: UserPlus, label: "Leads", menu: "crm_leads" },
    { to: "/admin/enrich", icon: Sparkles, label: "Enrich", menu: "crm_leads" },
    { to: "/admin/lists", icon: FolderOpen, label: "Lists", menu: "franchise_lists" },
    { to: "/admin/inbox", icon: Inbox, label: "Inbox", menu: "crm_inbox" },
    { to: "/admin/tasks", icon: CheckSquare, label: "Tasks", menu: "crm_tasks" },
  ];

  // Marketing group
  const marketingNavItems = [
    { to: "/admin/marketing/email-campaigns", icon: Send, label: "Email Campaigns", menu: "marketing" },
    { to: "/admin/marketing/dashboard", icon: BarChart3, label: "Mktg Dashboard", menu: "marketing" },
    { to: "/admin/marketing", icon: Megaphone, label: "Campaigns", menu: "marketing" },
    { to: "/admin/marketing/social-calendar", icon: Calendar, label: "Social Calendar", menu: "marketing" },
    { to: "/admin/marketing/reddit", icon: Radar, label: "Reddit Intel", menu: "marketing" },
    { to: "/admin/marketing/seo-audit", icon: Search, label: "SEO-AEO-GEO", menu: "marketing" },
    { to: "/admin/email-verify", icon: ShieldCheck, label: "Email Verify", menu: "crm_leads" },
    { to: "/admin/marketing/linkedin-data", icon: Linkedin, label: "LNKD-Data", menu: "marketing" },
    { to: "/admin/marketing/email-list", icon: Mail, label: "Email List", menu: "marketing" },
    { to: "/admin/marketing/email-personalize", icon: Wand2, label: "AI Personalize", menu: "marketing" },
  ];

  // 3. AI & Calls group (collapsed by default, CRM users)
  const aiCallsNavItems = [
    { to: "/admin/ai-calls", icon: Bot, label: "AI Calls", menu: "ai_calls" },
    { to: "/admin/dialer", icon: PhoneCall, label: "Dialer", menu: "ai_calls" },
    (isAdmin || isTeamLeader) && { to: "/admin/call-monitor", icon: Radio, label: "Live Monitor", menu: "call_monitor" },
    { to: "/admin/call-dashboard", icon: BarChart3, label: "Call Dashboard", menu: "ai_calls" },
    { to: "/admin/sequences", icon: Zap, label: "Sequences", menu: "ai_calls" },
  ].filter(Boolean);

  // 4. Content & Resources group (collapsed by default, all users)
  const contentNavItems = [
    hasDocumentAccess && { to: "/documents", icon: FolderOpen, label: "Documents", menu: "documents" },
    hasTemplateAccess && { to: "/admin/templates", icon: FileType, label: "Templates", menu: "templates" },
    { to: "/admin/knowledge-base", icon: BookOpen, label: "Knowledge Base" },
  ].filter(Boolean);

  // 5. Team Management group (collapsed by default, admin + team leader)
  const teamMgmtNavItems = [
    !isRecruitingTL && { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard", menu: "dashboard" },
    isTeamLeader && !isAdmin && { to: "/app/team-logs", icon: Users, label: "Team Logs", menu: "team_management" },
    { to: "/admin/logs", icon: ScrollText, label: "All Logs", menu: "team_management" },
    { to: "/admin/user-dashboard", icon: UserCircle, label: "User Dashboard", menu: "team_management" },
  ].filter(Boolean);

  // 6. Admin group (collapsed by default, admin only)
  const adminNavItems = [
    hasUserManagementAccess && { to: "/admin/users", icon: Users, label: "Users", menu: "admin_users" },
    hasTeamManagementAccess && { to: "/admin/teams", icon: Building2, label: "Teams", menu: "admin_teams" },
    isAdmin && { to: "/admin/forms", icon: FileStack, label: "Forms" },
    { to: "/admin/email-logs", icon: Mail, label: "Email Logs", menu: "admin_users" },
    isAdmin && { to: "/admin/jobdiva-matching", icon: Link2, label: "JD Matching" },
  ].filter(Boolean);

  // 7. Reports group (admin + team leader)
  const reportsNavItems = [
    !isRecruitingTL && isAdmin && { to: "/admin/reports/activity", icon: BarChart3, label: "Activity Report" },
    { to: "/admin/reports/summary-recruiters", icon: Users, label: "Sum. Recruiters", menu: "team_management" },
    { to: "/admin/reports", icon: FileText, label: "Daily Log Reports", menu: "team_management" },
    { to: "/admin/user-report", icon: FileUser, label: "User Report", menu: "team_management" },
  ].filter(Boolean);

  // NavItem / CollapsibleSection MUST keep a stable identity across renders. Declared as plain
  // inline arrow components they were a new component type every render, so React unmounted and
  // remounted every sidebar link on each re-render and a click straddling that swallowed the navigation.
  const NavItem = useCallback(({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      end
      onClick={handleNavClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-slate-600 hover:bg-slate-100"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="font-medium text-sm">{label}</span>
    </NavLink>
  ), [handleNavClick]);

  const CollapsibleSection = useCallback(({ title, icon: Icon, expanded, onToggle, children, badge }) => (
    <div className="mb-2">
      <button
        onClick={() => {
          if (navRef.current) {
            navScrollRef.current = navRef.current.scrollTop;
            shouldRestoreScroll.current = true;
          }
          onToggle();
        }}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-100 rounded-md transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-blue-600" />
          <span className="font-bold text-blue-600">{title}</span>
          {badge && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
              {badge}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-blue-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-blue-500" />
        )}
      </button>
      {expanded && (
        <div className="ml-2 mt-1 space-y-0.5 border-l-2 border-blue-200 pl-2">
          {children}
        </div>
      )}
    </div>
  ), []);

  return (
    <div className="min-h-screen bg-slate-50/30">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 header-glass z-50 flex items-center px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md hover:bg-slate-100"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <Link to={isRecruitingTL ? "/admin/logs" : (isAdmin || isTeamLeader) ? "/app/dashboard" : "/app/log"}>
          <img src="/logo.png" alt="DailyLog" className="h-8 ml-4 cursor-pointer hover:opacity-80 transition-opacity" />
        </Link>
        <div className="ml-3 flex flex-col items-start leading-tight">
          <span className="text-[10px] font-mono text-slate-400">{String(currentDateTime.getMonth() + 1).padStart(2, '0')}/{String(currentDateTime.getDate()).padStart(2, '0')}</span>
          <span className="text-[10px] font-mono text-slate-400">{['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][currentDateTime.getDay()]}</span>
          <span className="text-[10px] font-mono text-slate-500 font-semibold">{String(currentDateTime.getHours()).padStart(2, '0')}:{String(currentDateTime.getMinutes()).padStart(2, '0')}</span>
        </div>
      </div>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo, Clock, and Dialer */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Link to={isRecruitingTL ? "/admin/logs" : (isAdmin || isTeamLeader) ? "/app/dashboard" : "/app/log"} data-testid="sidebar-logo-link">
                <img src="/logo.png" alt="DailyLog" className="h-10 cursor-pointer hover:opacity-80 transition-opacity" />
              </Link>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-[10px] font-mono text-slate-400">{String(currentDateTime.getMonth() + 1).padStart(2, '0')}/{String(currentDateTime.getDate()).padStart(2, '0')}</span>
                <span className="text-[10px] font-mono text-slate-400">{['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][currentDateTime.getDay()]}</span>
                <span className="text-[10px] font-mono text-slate-500 font-semibold">{String(currentDateTime.getHours() % 12 || 12).padStart(2, '0')}:{String(currentDateTime.getMinutes()).padStart(2, '0')} {currentDateTime.getHours() >= 12 ? 'PM' : 'AM'}</span>
              </div>
            </div>

            {/* Phone Dialer Icon */}
            <button
              onClick={() => setDialerOpen(true)}
              className="p-2 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
              title="Open Phone Dialer"
              data-testid="dialer-button"
            >
              <Phone className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav ref={navRef} className="flex-1 p-3 space-y-1 overflow-y-auto">
            {/* 1. Daily Log - expanded by default */}
            <CollapsibleSection
              title="Daily Log"
              icon={ClipboardList}
              expanded={logsExpanded}
              onToggle={() => setLogsExpanded(!logsExpanded)}
            >
              {allowed(logsNavItems).map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </CollapsibleSection>

            {/* 2. CRM - collapsed by default (hidden for recruiting team) */}
            {hasCRMAccess && !isRecruitingTeam && (
              <CollapsibleSection
                title="CRM"
                icon={Users2}
                expanded={crmExpanded}
                onToggle={() => setCrmExpanded(!crmExpanded)}
              >
                {allowed(crmNavItems).map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </CollapsibleSection>
            )}

            {/* Marketing - collapsed by default */}
            <CollapsibleSection
              title="Marketing"
              icon={Megaphone}
              expanded={marketingExpanded}
              onToggle={() => setMarketingExpanded(!marketingExpanded)}
            >
              {allowed(marketingNavItems).map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </CollapsibleSection>

            {/* 3. AI & Calls - collapsed by default (hidden for recruiting team) */}
            {hasCRMAccess && !isRecruitingTeam && (
              <CollapsibleSection
                title="AI & Calls"
                icon={Bot}
                expanded={aiCallsExpanded}
                onToggle={() => setAiCallsExpanded(!aiCallsExpanded)}
              >
                {allowed(aiCallsNavItems).map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </CollapsibleSection>
            )}

            {/* 4. Content & Resources - collapsed by default (hidden for recruiting TLs) */}
            {!isRecruitingTeam && (
              <CollapsibleSection
                title="Resources"
                icon={BookOpen}
                expanded={contentExpanded}
                onToggle={() => setContentExpanded(!contentExpanded)}
              >
                {allowed(contentNavItems).map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </CollapsibleSection>
            )}

            {/* 5. Team Management - collapsed by default, admin + team leader */}
            {(isAdmin || isTeamLeader) && (
              <CollapsibleSection
                title="Team Management"
                icon={Users}
                expanded={teamMgmtExpanded}
                onToggle={() => setTeamMgmtExpanded(!teamMgmtExpanded)}
              >
                {allowed(teamMgmtNavItems).map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </CollapsibleSection>
            )}

            {/* 6. Admin - collapsed by default, admin only */}
            {isAdmin && (
              <CollapsibleSection
                title="Admin"
                icon={Cog}
                expanded={adminExpanded}
                onToggle={() => setAdminExpanded(!adminExpanded)}
              >
                {allowed(adminNavItems).map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </CollapsibleSection>
            )}

            {/* 7. Reports - admin + team leader */}
            {(isAdmin || isTeamLeader) && (
              <CollapsibleSection
                title="Reports"
                icon={BarChart3}
                expanded={reportsExpanded}
                onToggle={() => setReportsExpanded(!reportsExpanded)}
              >
                {allowed(reportsNavItems).map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </CollapsibleSection>
            )}

            {/* 7. Settings - standalone, admin only */}
            {isAdmin && (
              <NavItem to="/admin/settings" icon={Cog} label="Settings" />
            )}
          </nav>

          {/* Quick Add Company — always visible for CRM users */}
          {hasCRMAccess && (
            <div className="px-3 pb-2">
              <button
                onClick={() => navigate('/admin/companies', { state: { openAddCompany: true } })}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors shadow-sm"
              >
                <Plus className="h-4 w-4 shrink-0" />
                Add Company
              </button>
            </div>
          )}

          {/* DB Mode Badge (admin only) */}
          {isAdmin && dbInfo && (
            <div className="px-3 pb-1 relative" ref={tooltipRef}>
              <button
                onClick={() => setDbTooltipOpen(v => !v)}
                className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide transition-colors ${
                  dbInfo.db_mode === "prod"
                    ? "bg-red-100 text-red-700 hover:bg-red-200 border border-red-300"
                    : "bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${dbInfo.db_mode === "prod" ? "bg-red-500" : "bg-amber-500"}`} />
                {dbInfo.db_mode === "prod" ? "PROD DB" : "LOCAL DB"}
                <span className="opacity-60 text-[10px]">({dbInfo.db_name})</span>
              </button>

              {dbTooltipOpen && (
                <div className="absolute bottom-full left-3 right-3 mb-2 bg-slate-900 text-white text-xs rounded-lg p-3 shadow-xl z-50">
                  <p className="font-semibold mb-1.5">Switch Database</p>
                  <p className="text-slate-300 mb-2">Run one of these commands on the RTX8000 server, then restart:</p>
                  <div className="space-y-1.5">
                    <div>
                      <p className="text-amber-400 font-mono text-[10px] mb-0.5">LOCAL DB:</p>
                      <code className="bg-slate-800 px-2 py-1 rounded text-[10px] block break-all">make local</code>
                    </div>
                    <div>
                      <p className="text-red-400 font-mono text-[10px] mb-0.5">PROD DB:</p>
                      <code className="bg-slate-800 px-2 py-1 rounded text-[10px] block break-all">make prod</code>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-slate-700">
                    <p className="text-slate-400 text-[10px]">Dir: /home/admin/claude/JTCRM</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Section */}
          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                {user?.phone && (
                  <p className="text-xs text-slate-600 truncate mt-0.5 flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {user.phone.replace(/^1?(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3')}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="shrink-0"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0">
        <div className="p-6 md:p-8 max-w-[1600px]">
          <Outlet />
        </div>
      </main>
      
      {/* Version Badge - Fixed Position */}
      <VersionBadge />

      {/* Notification Bell - Fixed top-right, plays sound + ping on new notifications */}
      {hasCRMAccess && <NotificationBell />}

      {/* Inbound Call Manager - handles incoming calls globally */}
      {hasCRMAccess && <InboundCallManager />}
      
      {/* Recent Activity Panel - slide-out sidebar for quick access */}
      {hasCRMAccess && <RecentActivityPanel />}
      
      {/* Phone Dialer */}
      <PhoneDialer isOpen={dialerOpen} onClose={() => setDialerOpen(false)} />
    </div>
  );
}
